import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Table,
  Modal,
  Form,
  Input,
  Space,
  Popconfirm,
  message,
  Select,
  Row,
  Col,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTranslators,
  createTranslator,
  updateTranslator,
  deleteTranslator,
} from "../../features/Translators/translatorSlice";
import { fetchLanguages } from "../../features/Languages/languageSlice";
import { fetchServices } from "../../features/Services/serviceSlice";

const { Option } = Select;

const Translators = () => {
  const dispatch = useDispatch();
  const { translators = [], loading: translatorsLoading, pagination } = useSelector(
    (state) => state.translators
  );
  const { languages = [], loading: languagesLoading } = useSelector(
    (state) => state.languages
  );
  const { services = [], loading: servicesLoading } = useSelector(
    (state) => state.services
  );

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [editingTranslator, setEditingTranslator] = useState(null);
  const [searchText, setSearchText] = useState("");

  const [filters, setFilters] = useState({
    source_language_id: null,
    target_language_id: null,
    service_id: null,
    search: "",
    page: 1,
    per_page: 10,
  });

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilters((prev) => ({ ...prev, search: value, page: 1 }));
    }, 500),
    []
  );

  useEffect(() => {
    console.log('Filters:', filters); // Debug filters
    dispatch(fetchLanguages());
    dispatch(fetchServices());
    dispatch(fetchTranslators(filters));
  }, [dispatch, filters]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
      page: 1,
    }));
  };

  const handleAdd = () => {
    setEditingTranslator(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (translator) => {
    setEditingTranslator(translator);
    form.setFieldsValue({
      ...translator,
      native_language_id: translator.native_language?.id,
      languages: translator.languages?.map((lang) => lang.id),
      pricing: translator.pricing?.map((price) => ({
        ...price,
        key: price.id || Math.random(),
      })),
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    dispatch(deleteTranslator(id))
      .unwrap()
      .then(() => message.success("Translator deleted successfully"))
      .catch(() => message.error("Failed to delete translator"));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingTranslator) {
        await dispatch(
          updateTranslator({ id: editingTranslator.id, ...values })
        ).unwrap();
        message.success("Translator updated successfully");
      } else {
        await dispatch(createTranslator(values)).unwrap();
        message.success("Translator created successfully");
      }
      setIsModalVisible(false);
    } catch (error) {
      if (error.payload?.errors) {
        const fieldErrors = Object.keys(error.payload.errors).map((field) => ({
          name: field.split("."),
          errors: error.payload.errors[field],
        }));
        form.setFields(fieldErrors);
        if (error.payload.message) {
          message.error(error.payload.message);
        }
      } else {
        message.error(error.message || "An error occurred");
      }
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Native Language",
      dataIndex: ["native_language", "name"],
      key: "native_language",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this translator?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" icon={<DeleteOutlined />} danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const paginationConfig = {
    current: pagination.current_page || filters.page,
    pageSize: pagination.per_page || filters.per_page,
    total: pagination.total || 0,
    onChange: (page, pageSize) => {
      setFilters((prev) => ({ ...prev, page, per_page: pageSize }));
    },
  };

  return (
    <div style={{ padding: "20px" }}>
      <h3>Translators Management</h3>
      <Space style={{ marginBottom: "16px" }}>
        <Button className="add-button" type="primary" onClick={handleAdd}>
          Add Translator
        </Button>
        <Input.Search
          placeholder="Search translators"
          value={searchText}
          onChange={handleSearchChange}
          style={{ width: 200 }}
          allowClear
        />
      </Space>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Select
            placeholder="Filter by Source Language"
            style={{ width: "100%" }}
            onChange={(value) => handleFilterChange("source_language_id", value)}
            allowClear
          >
            {languages.map((lang) => (
              <Option key={lang.id} value={lang.id}>
                {lang.name} ({lang.code})
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={8}>
          <Select
            placeholder="Filter by Target Language"
            style={{ width: "100%" }}
            onChange={(value) => handleFilterChange("target_language_id", value)}
            allowClear
          >
            {languages.map((lang) => (
              <Option key={lang.id} value={lang.id}>
                {lang.name} ({lang.code})
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={8}>
          <Select
            placeholder="Filter by Service"
            style={{ width: "100%" }}
            onChange={(value) => handleFilterChange("service_id", value)}
            allowClear
          >
            {services.map((service) => (
              <Option key={service.id} value={service.id}>
                {service.name}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Table
        dataSource={translators}
        columns={columns}
        loading={translatorsLoading}
        rowKey="id"
        pagination={paginationConfig}
        scroll={{ x: true }}
      />

      <Modal
        title={editingTranslator ? "Edit Translator" : "Add Translator"}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="native_language_id" label="Native Language" rules={[{ required: true }]}>
            <Select placeholder="Select native language" loading={languagesLoading}>
              {languages.map((lang) => (
                <Option key={lang.id} value={lang.id}>
                  {lang.name} ({lang.code})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="languages" label="Languages" rules={[{ required: true }]}>
            <Select mode="multiple" placeholder="Select languages" loading={languagesLoading}>
              {languages.map((lang) => (
                <Option key={lang.id} value={lang.id}>
                  {lang.name} ({lang.code})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.List name="pricing">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      borderBottom: "1px solid #e8e8e8",
                      paddingBottom: "16px",
                      marginBottom: "16px",
                    }}
                  >
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, "source_language_id"]}
                          label="Source Language"
                        >
                          <Select placeholder="Select source language" loading={languagesLoading}>
                            {languages.map((lang) => (
                              <Option key={lang.id} value={lang.id}>
                                {lang.name} ({lang.code})
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, "target_language_id"]}
                          label="Target Language"
                        >
                          <Select placeholder="Select target language" loading={languagesLoading}>
                            {languages.map((lang) => (
                              <Option key={lang.id} value={lang.id}>
                                {lang.name} ({lang.code})
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item {...restField} name={[name, "service_id"]} label="Service">
                          <Select placeholder="Select service" loading={servicesLoading}>
                            {services.map((service) => (
                              <Option key={service.id} value={service.id}>
                                {service.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, "price_per_word"]}
                          label="Price Per Word"
                        >
                          <Input type="number" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          {...restField}
                          name={[name, "price_per_page"]}
                          label="Price Per Page"
                        >
                          <Input type="number" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Col>
                    </Row>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Pricing
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default Translators;